/**
 * CashFlowsBuilder - Builds payment-by-payment cash flow records for candidates
 *
 * Cash flows represent the actual money movement on each payment date:
 * - Revenue collected (portion of annual revenue)
 * - Existing debt service
 * - New bond payments (principal + interest)
 * - Net cash flow and cumulative tracking
 *
 * This is a derived data structure built from revenue map, date records, and bond series.
 */

import { ExistingDebtBuilder } from '~/builders/existing-debt/ExistingDebtBuilder';
import type {
  BondPaymentCashFlowRecord,
  Candidate,
  CashFlow,
  Configuration,
  Project,
  ProjectDateRecordByPaymentDate,
} from '~/types';

export class CashFlowsBuilder {
  /**
   * Initialize cash flow records for a candidate
   *
   * Creates the basic cash flow structure with known data:
   * - Payment dates and revenue portions from date records
   * - Existing debt service by payment date
   * - Bond series placeholders (no calculations yet)
   */
  static initializeCashFlows(
    candidate: Candidate,
    project: Project,
    configuration: Configuration
  ): void {
    const { revenueMap } = candidate;
    const dateRecordsMapByPaymentDate = project.dateElements?.dateRecordsMapByPaymentDate || {};
    const existingDebtProjects = project.existingDebtProjects || [];

    // Create combined debt service on-demand (it's not stored in project.existingDebtProjects)
    // This matches the pattern used in candidate-builder.ts and revenue-map-builder.ts
    const combinedDebtService =
      existingDebtProjects.length > 0
        ? ExistingDebtBuilder.combineDebtProjects(existingDebtProjects)
        : null;

    const bondSeries = configuration.bondSeries;

    if (!dateRecordsMapByPaymentDate || Object.keys(dateRecordsMapByPaymentDate).length === 0) {
      candidate.cashFlows = [];
      return;
    }

    // Find the earliest bond issue date to determine when cash flows should start
    let earliestBondIssueDate: string | null = null;
    if (bondSeries && bondSeries.length > 0) {
      earliestBondIssueDate = bondSeries.reduce((earliest, bond) => {
        if (!earliest || bond.issueDate < earliest) {
          return bond.issueDate;
        }
        return earliest;
      }, bondSeries[0].issueDate);
    }

    const cashFlows: CashFlow[] = [];
    const cumulativeExistingDebtRecords = combinedDebtService?.existingDebtPaymentRecords || [];

    // Create cash flows for each payment date in the date records map
    Object.entries(dateRecordsMapByPaymentDate).forEach(([, paymentDateRecord]) => {
      const {
        paymentDate,
        taxYear,
        revenueCollectionByDatePercentage,
        isInterestOnly,
        fiscalYearEndDate,
        isLastPaymentInFiscalYear,
      } = paymentDateRecord as ProjectDateRecordByPaymentDate;

      // Skip payment dates that occur before the earliest bond issue date
      // This ensures we don't generate cash flows before any bonds are active
      if (earliestBondIssueDate && paymentDate <= earliestBondIssueDate) {
        return; // Skip this payment date as it occurs before or on the bond issue date
      }

      const revenueMapEntry = revenueMap[taxYear];

      if (!revenueMapEntry) {
        return; // Skip if no revenue map entry for this tax year
      }

      // Calculate revenue portion for this payment date (this is known data)
      const revenuePortionForPaymentDate = Math.round(
        revenueMapEntry.revenue * revenueCollectionByDatePercentage
      );

      // Get millage rate for this tax year
      const millageRate = candidate.millageInformation?.millageMap[taxYear] || 0;

      // Get existing debt payment for this date (simplified distribution for now)
      const cumulativeExistingDebtRecord = cumulativeExistingDebtRecords.find(
        (record) => record.paymentDate === paymentDate
      );
      const existingDebtPortionForPaymentDate = cumulativeExistingDebtRecord?.totalPayment || 0;
      const existingDebtPrincipalPortion = cumulativeExistingDebtRecord?.principalPayment || 0;
      const existingDebtInterestPortion = cumulativeExistingDebtRecord?.totalPaymentInterest || 0;

      // Calculate revenue minus existing debt (this is known)
      const revenueMinusExistingDebt =
        revenuePortionForPaymentDate - existingDebtPortionForPaymentDate;

      // Initialize bond payment records (no calculations yet)
      const bondPayments: BondPaymentCashFlowRecord[] = bondSeries.map((bond) => {
        // Check if this bond has a payment scheduled for this date
        const bondPaymentMapEntry =
          bond.bondPaymentMap &&
          Object.values(bond.bondPaymentMap).find((entry) => entry.paymentDate === paymentDate);
        const isActive = !!bondPaymentMapEntry;

        return {
          bondSeriesId: bond.id,
          bondName: bond.name, // Add individual bond name
          bondSeriesName: bond.name,
          bondCFIssueDate: bond.issueDate,
          isActive, // Only active if bond has payment scheduled for this date
          isInterestOnly: isInterestOnly || false,
          principal: 0, // Will be calculated during distribution processing
          interestRate: 0, // Will be set from BondPaymentDateMap during distribution
          newInterest: 0, // Will be calculated during distribution processing
          futureInterestObligation: 0, // Will be calculated during distribution processing
          totalInterest: 0, // Will be calculated during distribution processing
          totalPayment: 0, // Will be calculated during distribution processing
        };
      });

      // Initialize cash flow with comprehensive contextual data
      cashFlows.push({
        // Date and fiscal year context
        paymentDate,
        relatedTaxBillYear: taxYear,
        relatedFiscalYearEndingDate: fiscalYearEndDate as string,
        isFinalFiscalYearPayment: isLastPaymentInFiscalYear as boolean,
        isActive: bondPayments.some((bp) => bp.isActive),

        // Millage and revenue context
        millageRate,
        totalRevenueForTaxYear: revenueMapEntry.revenue,
        paymentRevenuePercentage: revenueCollectionByDatePercentage,
        paymentRevenuePortion: revenuePortionForPaymentDate,

        // Revenue flow tracking
        drfStartingBalance: 0, // Will be calculated during DRF processing
        debtPaymentAmount: existingDebtPortionForPaymentDate,
        existingDebtPrincipal: existingDebtPrincipalPortion,
        existingDebtInterest: existingDebtInterestPortion,
        revenueMinusExistingDebt,
        isInterestOnly: isInterestOnly || false,
        interestObligation: 0, // Will be calculated during distribution processing
        availableForBonds: revenueMinusExistingDebt, // Initially same as revenue minus existing debt

        // Bond payment details
        bondPayments,
        totalBondPayment: 0, // Will be calculated during distribution processing

        // DRF tracking
        drfAdjustment: 0, // Will be calculated during DRF processing
        drfEndingBalance: 0, // Will be calculated during DRF processing
        drfNetChange: 0, // Will be calculated during DRF processing
        drfNetRevenue: revenuePortionForPaymentDate, // Initially same as revenue portion
      });
    });

    candidate.cashFlows = cashFlows;
  }
}
